// "Planned hike from waypoints" is routed, not drawn: each consecutive pair of kept waypoints
// (file order) is one leg through the planner's own GraphHopper routing, and the legs are
// joined into the day's line. A leg that can't be routed fails the whole file — it is never
// patched with a straight line, so what gets saved is always a walkable route.
// Pure apart from the injected router, so splitting, joining and failure are pinned by Jest
// (importWaypointRoute.test.ts).

import type { ImportPoint } from './importTypes';

/**
 * One leg's route: `[lng, lat]` in, the routed `[lng, lat, ele?]` coordinates out. Throws when
 * there is no route (offline, an HTTP error, or GraphHopper finding none).
 */
export type WaypointRouter = (start: number[], end: number[]) => Promise<number[][]>;

/** The planner's default route preference (PlanTrailModule `routePreference`): walking. */
export const WAYPOINT_ROUTE_PROFILE = 'hike';

/** At most this many legs are asked for at once. */
export const WAYPOINT_ROUTE_CONCURRENCY = 4;

/** Thrown when any leg between a file's waypoints can't be routed; the file isn't imported. */
export class WaypointRouteError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WaypointRouteError';
  }
}

const samePlace = (a: number[], b: number[]): boolean => a[0] === b[0] && a[1] === b[1];

/**
 * The legs to route: every consecutive pair, in order, skipping a waypoint that sits exactly
 * on the one before it (there's nothing to route between them).
 */
export function waypointLegs(points: ImportPoint[]): Array<[number[], number[]]> {
  const stops: number[][] = [];
  for (const p of points) {
    const c = [p.lon, p.lat];
    if (!stops.length || !samePlace(stops[stops.length - 1], c)) stops.push(c);
  }
  return stops.slice(1).map((end, i) => [stops[i], end]);
}

/**
 * The legs end to end as one line. A leg's first point is the previous leg's last (both are
 * the same waypoint, snapped the same way), so it is dropped there rather than repeated.
 */
export function joinLegs(legs: number[][][]): number[][] {
  const out: number[][] = [];
  for (const leg of legs) {
    leg.forEach((c, i) => {
      if (i === 0 && out.length && samePlace(out[out.length - 1], c)) return;
      out.push(c);
    });
  }
  return out;
}

/** Routed coordinates as import points, keeping the router's elevation when it has one. */
export const routedPoints = (coords: number[][]): ImportPoint[] =>
  coords.map(c => (Number.isFinite(c[2]) ? { lon: c[0], lat: c[1], ele: c[2] } : { lon: c[0], lat: c[1] }));

/**
 * The day's line through `points`, routed leg by leg (a few at a time) and joined. Throws
 * WaypointRouteError when there are fewer than two places to route between or any leg fails.
 */
export async function routeThroughWaypoints(
  points: ImportPoint[],
  route: WaypointRouter,
  concurrency = WAYPOINT_ROUTE_CONCURRENCY
): Promise<ImportPoint[]> {
  const legs = waypointLegs(points);
  if (!legs.length) throw new WaypointRouteError('fewer than two waypoints to route between');
  const routed: number[][][] = new Array(legs.length);
  let next = 0;
  let failed: unknown = null;
  const worker = async (): Promise<void> => {
    while (failed === null && next < legs.length) {
      const i = next++;
      try {
        const coords = await route(legs[i][0], legs[i][1]);
        if (coords.length < 2) throw new Error('empty route');
        routed[i] = coords;
      } catch (e) {
        if (failed === null) failed = e;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(Math.max(1, concurrency), legs.length) }, worker));
  if (failed !== null) {
    throw new WaypointRouteError(failed instanceof Error ? failed.message : String(failed));
  }
  return routedPoints(joinLegs(routed));
}

/**
 * Runs tasks at most `max` at a time, in the order they're given — the background planner
 * shares one across the whole screen, so several files never ask for more than `max` legs.
 */
export function concurrencyLimit(max = WAYPOINT_ROUTE_CONCURRENCY): <T>(task: () => Promise<T>) => Promise<T> {
  let active = 0;
  const waiting: Array<() => void> = [];
  return async <T>(task: () => Promise<T>): Promise<T> => {
    // A finishing task hands its slot straight to the next one waiting.
    if (active >= max) await new Promise<void>(resolve => waiting.push(resolve));
    else active++;
    try {
      return await task();
    } finally {
      const next = waiting.shift();
      if (next) next();
      else active--;
    }
  };
}
